import { McpChatPage } from '@backstage-community/plugin-mcp-chat';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import { ConsentPage } from '@backstage/plugin-auth';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { UnprocessedEntitiesContent } from '@backstage/plugin-catalog-unprocessed-entities';
import { catalogUnprocessedEntitiesPlugin } from '@backstage/plugin-catalog-unprocessed-entities';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import { SignalsDisplay } from '@backstage/plugin-signals';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import {
  ExpandableNavigation,
  LightBox,
  ReportIssue,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  palettes,
} from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { Navigate, Route } from 'react-router-dom';

import { DxSetupPage } from '@internal/backstage-plugin-dx-setup';
import { createFrameworksColumn } from '@internal/backstage-plugin-framework-versions';
import { OrgPage } from '@internal/backstage-plugin-org-manager';
import { PrototypeViewerPage } from '@internal/backstage-plugin-prototype-viewer';
import { ScoresPage } from '@internal/backstage-plugin-scores';
import { EditWiki } from '@internal/backstage-plugin-techdocs-addon-wiki';

import { apis } from './apis';
import { Root } from './components/Root';
import { entityPage } from './components/catalog/EntityPage';
import { HomePage } from './components/home/HomePage';
import { searchPage } from './components/search/SearchPage';
import { WorkstationSetupPage } from './components/workstation-setup/WorkstationSetupPage';
import { icons } from './icons';
import { templateGroups, templateHeaderOptions } from './scaffolder/templates';
import { catalogTranslations } from './translations/catalog';
import { scaffolderTranslations } from './translations/scaffolder';

const sidebarOverrides = {
  BackstageSidebarItem: {
    styleOverrides: {
      label: { width: '140px' },
    },
  },
};

const customLightTheme = createUnifiedTheme({
  palette: palettes.light,
  components: sidebarOverrides,
});

const customDarkTheme = createUnifiedTheme({
  palette: palettes.dark,
  components: sidebarOverrides,
});

const app = createApp({
  apis,
  icons,
  plugins: [catalogUnprocessedEntitiesPlugin],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        provider={{
          id: 'microsoft-auth-provider',
          title: 'Microsoft',
          message: 'Sign in using Microsoft',
          apiRef: microsoftAuthApiRef,
        }}
      />
    ),
  },
  themes: [
    {
      id: 'light',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'dark',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  __experimentalTranslations: {
    resources: [catalogTranslations, scaffolderTranslations],
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/home" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          initiallySelectedFilter="all"
          columns={ctx => [
            ...CatalogTable.defaultColumnsFunc(ctx),
            createFrameworksColumn(),
          ]}
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/api-docs"
      element={<ApiExplorerPage initiallySelectedFilter="all" />}
    />
    <Route path="/docs" element={<TechDocsIndexPage initialFilter="all" />}>
      <DefaultTechDocsHome initialFilter="all" />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ExpandableNavigation />
        <LightBox />
        <ReportIssue />
        <Mermaid />
        <EditWiki />
      </TechDocsAddons>
    </Route>
    <Route
      path="/self-service"
      element={
        <ScaffolderPage
          headerOptions={templateHeaderOptions}
          groups={templateGroups}
        />
      }
    />
    <Route path="/org" element={<OrgPage />} />
    <Route
      path="/scores"
      element={<ScoresPage initiallySelectedFilter="all" />}
    />
    <Route path="/prototypes" element={<PrototypeViewerPage />} />
    <Route path="/tech-radar" element={<TechRadarPage pageTitle="" />} />
    <Route path="/ai-assistant" element={<McpChatPage />} />
    <Route
      path="/devtools"
      element={
        <RequirePermission permission={devToolsAdministerPermission}>
          <DevToolsPage
            contents={[
              {
                path: 'unprocessed-entities',
                title: 'Unprocessed Entities',
                children: <UnprocessedEntitiesContent />,
              },
            ]}
          />
        </RequirePermission>
      }
    />
    <Route path="/workstation-setup" element={<WorkstationSetupPage />} />
    <Route path="/dx-setup" element={<DxSetupPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/oauth2/authorize/:sessionId" element={<ConsentPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
